import React from "react";
import GraphOrigDestEvolution from "../components/GraphOrigDestEvolution";
import HeaderNavBar from "../components/HeaderNavBar";
import SearchResultHeader from "../components/SearchResultHeader";
import * as APIUtils from "../api/api-utils";
import * as GraphAPI from "../api/graph-api";
import Select, { ActionMeta } from "react-select";
import { RouteComponentProps, Redirect } from "react-router-dom";
import { StationDefinition } from "../api/api-data-model";
import GraphOrigDestPerHour from "../components/GraphOrigDestPerHour";
import GraphOrigDestPerDay from "../components/GraphOrigDestPerDay";
import { LocationDescriptor } from "history";
import { StaticContext } from "react-router";
import AppNavigationState from "../utils/AppNavigationState";
import { WithTranslationLangProps } from "../utils/i18next/with-translation-keys";
import { withTranslation } from "react-i18next";
import DownloadButton from "../components/DownloadButton";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";

type RouterParams = { from: string; to: string };

type DataKey = {
  value: string;
  label: string;
  chartLabel: string;
  baseValue: number;
};

type LabelValue = { label: string; value: number };

interface IAnalyseJourneyPageProps
  extends WithTranslationLangProps,
    RouteComponentProps<
      RouterParams,
      StaticContext,
      AppNavigationState | undefined
    > {}

interface IAnalyseJourneyPageState {
  isEvolutionGraphLoading: boolean;
  isPerHourGraphLoading: boolean;
  isPerDayGraphLoading: boolean;
  evolutionGraphData?: GraphAPI.GetODEvolutionResult;
  perHourGraphData?: GraphAPI.GetODPerHourResult;
  perDayGraphData?: GraphAPI.GetODPerDayResult;
  selectedDataKey: DataKey;
  modes?: LabelValue[];
  selectedMode?: LabelValue;
  years?: LabelValue[];
  evolutionYears?: LabelValue[];
  selectedYear?: LabelValue;
  perHourSelectedYear?: LabelValue;
  perDaySelectedYear?: LabelValue;
  from?: StationDefinition;
  to?: StationDefinition;
  redirect?: LocationDescriptor<AppNavigationState>;
  rangeSelectedYears?: number[];
  marks?: LabelValue[];
  start: number;
  showAlertEvolution: boolean;
  showAlertHour: boolean;
  showAlertDay: boolean;
}

class AnalyseJourneyPage extends React.Component<
  IAnalyseJourneyPageProps,
  IAnalyseJourneyPageState
> {
  private reliabOptions: DataKey[];
  private localeUsedForOptions?: string;
  private interval: NodeJS.Timeout | undefined;

  constructor(props: IAnalyseJourneyPageProps) {
    super(props);
    this.handleNextTrainClicked = this.handleNextTrainClicked.bind(this);
    this.handleMainDataKeyChanged = this.handleMainDataKeyChanged.bind(this);
    this.handleModeChanged = this.handleModeChanged.bind(this);
    this.handleYearChanged = this.handleYearChanged.bind(this);
    this.handleRangeSliderChanged = this.handleRangeSliderChanged.bind(this);
    this.onLanguageChanged = this.onLanguageChanged.bind(this);
    this.updateReliabOptions = this.updateReliabOptions.bind(this);
    this.checkDataAvailability = this.checkDataAvailability.bind(this);
    this.stopInterval = this.stopInterval.bind(this);

    this.localeUsedForOptions = undefined;
    this.reliabOptions = [];
    this.updateReliabOptions();
    this.state = {
      isEvolutionGraphLoading: true,
      isPerHourGraphLoading: true,
      isPerDayGraphLoading: true,
      from: props.location.state?.searchContext?.fromStation,
      to: props.location.state?.searchContext?.toStation,
      selectedDataKey: this.reliabOptions[0],
      start: Date.now(),
      showAlertEvolution: false,
      showAlertHour: false,
      showAlertDay: false,
    };
  }

  updateReliabOptions() {
    this.localeUsedForOptions = this.props.i18n.resolvedLanguage;
    let options = [];
    for (let i of [5, 10, 15, 20, 25, 30, 60, 120]) {
      options.push({
        value: "reliability" + i + "min",
        label: this.props.t("X_minutes", {
          value: i,
        }),
        chartLabel: this.props.t("chart_label_trip_reliab_X_min", {
          value: i,
        }),
        baseValue: i,
      });
    }
    this.reliabOptions = options;
  }

  componentDidMount() {
    if (this.props.i18n.resolvedLanguage !== this.localeUsedForOptions) {
      this.onLanguageChanged();
    }

    this.interval = setInterval(this.checkDataAvailability, 1000); // Vérifie toutes les secondes

    GraphAPI.getOrigDestAllGraphs(
      this.props.match.params.from,
      this.props.match.params.to
    ).then((result) => {
      result.hours_per_mode.unshift({
        years: result.hours,
        mode: this.props.t("any_rail_service"),
      });
      result.days_per_mode.unshift({
        years: result.days,
        mode: this.props.t("any_rail_service"),
      });
      result.evolutionPerMode.unshift({
        years: result.evolution,
        mode: this.props.t("any_rail_service"),
      });
      let modes = result.modes.map((mode, index) => {
        return { value: index+1, label: mode };
      });
      modes.unshift({
        value: 0,
        label: this.props.t("any_rail_service"),
      });
      let years = result.years.map((year, index) => {
        return { value: index, label: year };
      });
      let rangeSelectedYears = years.map(yearDef => { return Number(yearDef.label) % 2000 });
      this.setState({
        isPerHourGraphLoading: false,
        isPerDayGraphLoading: false,
        isEvolutionGraphLoading: false,
        perHourGraphData: { hours: result.hours, hours_per_mode: result.hours_per_mode, query: result.query },
        perDayGraphData: { days: result.days, days_per_mode: result.days_per_mode, query: result.query },
        evolutionGraphData: { evolution: result.evolution, evolutionPerMode: result.evolutionPerMode, query: result.query },
        modes: modes,
        selectedMode: modes[0],
        years: years,
        evolutionYears: years,
        rangeSelectedYears: rangeSelectedYears.length > 1 ? [rangeSelectedYears[0], rangeSelectedYears[1]] : [rangeSelectedYears[0]],
        marks: rangeSelectedYears.map(value => { return { value: value, label: "20" + value.toString() } }),
        selectedYear: years[0],
        perHourSelectedYear: years[0],
        perDaySelectedYear: years[0],
        from: result.query.from,
        to: result.query.to,
      });
    });
  }

  componentWillUnmount() {
    this.stopInterval();
  }

  checkDataAvailability() {
    const timeout = 30 * 1000; // 30 secondes
    let current = Date.now();
    let condition = (current - this.state.start) > timeout;
    if (condition) {
      this.stopInterval();
    }

    if (this.state.perHourGraphData === undefined) {
      this.setState({
        isPerHourGraphLoading: true,
      });
      if (condition) {
        this.setState({
          isPerHourGraphLoading: false,
          showAlertHour: true,
        });
      }
    }
    if (this.state.perDayGraphData === undefined) {
      this.setState({
        isPerDayGraphLoading: true,
      });
      if (condition) {
        this.setState({
          isPerDayGraphLoading: false,
          showAlertDay: true,
        });
      }
    }
    if (this.state.evolutionGraphData === undefined) {
      this.setState({
        isEvolutionGraphLoading: true,
      });
      if (condition) {
        this.setState({
          isEvolutionGraphLoading: false,
          showAlertEvolution: true,
        });
      }
    }
  }

  stopInterval() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = undefined;
    }
  }

  onLanguageChanged() {
    this.updateReliabOptions();
    let currentDataKey = this.reliabOptions[0];
    for (let dataKey of this.reliabOptions) {
      if (dataKey.baseValue === this.state.selectedDataKey.baseValue) {
        currentDataKey = dataKey;
        break;
      }
    }
    this.setState({
      selectedDataKey: currentDataKey,
    });
  }

  componentDidUpdate(
    prevProps: IAnalyseJourneyPageProps,
    _prevState: IAnalyseJourneyPageState
  ) {
    if (
      prevProps.t !== this.props.t ||
      this.props.i18n.resolvedLanguage !== this.localeUsedForOptions
    ) {
      this.onLanguageChanged();
    }
  }

  private handleMainDataKeyChanged(selectedVal: any, _actionMeta: ActionMeta<Object>) {
    this.setState({
      selectedDataKey: selectedVal,
    });
  }

  private handleModeChanged(selectedMode: any, _actionMeta: ActionMeta<Object>) {
    this.setState({
      selectedMode: selectedMode,
      perHourSelectedYear: this.state.selectedYear,
      perDaySelectedYear: this.state.selectedYear,
    });
    
    if (selectedMode) {
      let evolutionYears: LabelValue[] = [];
      if (this.state.evolutionGraphData) {
        if (selectedMode.value in this.state.evolutionGraphData.evolutionPerMode) {
          evolutionYears = this.state.evolutionGraphData.evolutionPerMode[
            selectedMode.value
          ].years.map((yearDef, index) => {
            return { value: index, label: yearDef.year };
          });
        }
      }
      this.setState({
        evolutionYears: evolutionYears
      });

      let rangeSelectedYears = evolutionYears?.map(yearDef => { return Number(yearDef.label) % 2000 });
      if (rangeSelectedYears) {
        this.setState({
          rangeSelectedYears: rangeSelectedYears.length > 1 ? [rangeSelectedYears[0], rangeSelectedYears[1]] : [rangeSelectedYears[0]],
          marks: rangeSelectedYears.map(value => { return { value: value, label: "20" + value.toString() } }),
        });
      }

      if (this.state.selectedYear) {
        if (
          (this.state.perHourGraphData)
          && (this.state.perHourGraphData.hours_per_mode[selectedMode.value].years[this.state.selectedYear.value].data.length === 0)
        ) {
            this.setState({ perHourSelectedYear: undefined });
        }
        if (
          (this.state.perDayGraphData)
          && (this.state.perDayGraphData.days_per_mode[selectedMode.value].years[this.state.selectedYear.value].data.length === 0)
        ) {
            this.setState({ perDaySelectedYear: undefined });
        }
      }
    }
  }

  private handleYearChanged(selectedYear: any, _actionMeta: ActionMeta<Object>) {
    this.setState({
      selectedYear: selectedYear,
      perHourSelectedYear: selectedYear,
      perDaySelectedYear: selectedYear,
    });
    
    if (
      (this.state.perHourGraphData) && (this.state.selectedMode)
      && (this.state.perHourGraphData.hours_per_mode[this.state.selectedMode.value].years[selectedYear.value].data.length === 0)
    ) {
        this.setState({ perHourSelectedYear: undefined });
    }
    if (
      (this.state.perDayGraphData) && (this.state.selectedMode)
      && (this.state.perDayGraphData.days_per_mode[this.state.selectedMode.value].years[selectedYear.value].data.length === 0)
    ) {
        this.setState({ perDaySelectedYear: undefined });
    }
  }

  private handleNextTrainClicked() {
    let from = this.props.match.params.from;
    let to = this.props.match.params.to;
    let searchDate = this.props.location.state?.searchContext?.searchDate;
    if (!searchDate) {
      searchDate = new Date();
    }
    let date = APIUtils.dateToAPIString(searchDate);

    let stateObject: AppNavigationState = {
      searchContext: {
        fromStation: this.state.from,
        toStation: this.state.to,
        searchDate: searchDate,
      },
    };
    let redirect = {
      pathname: `/journey/search/${from}/${to}/${date}`,
      state: stateObject,
    };
    this.setState({ redirect: redirect });
  }

  private handleRangeSliderChanged(_event: Event, newValue: number | number[]) {
    if (typeof newValue === "number") {
      this.setState({
        rangeSelectedYears: [newValue],
      });
    }
    else {
      this.setState({
        rangeSelectedYears: newValue,
      });
    }
  }

  render() {
    if (this.state.redirect) return <Redirect push to={this.state.redirect} />;

    let selectedChartLines = [
      {
        key: this.state.selectedDataKey.value,
        label: this.state.selectedDataKey.chartLabel,
      },
      {
        key: "avgReliability" + this.state.selectedDataKey.baseValue + "min",
        label: this.props.t("reliability_X_min", {
          value: this.state.selectedDataKey.baseValue,
        }),
      },
    ];

    return (
      <>
        <HeaderNavBar />
        <SearchResultHeader
          date={this.props.location.state?.searchContext?.searchDate}
          from={this.state.from}
          to={this.state.to}
          showDate={false}
        />
        <a
          href="#top"
          title="Aller en haut de la page"
          id="top-link"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 6" fill="currentcolor">
            <path d="M12 6H0l6-6z"></path>
          </svg>
        </a>
        <div className="container">
          <div className="row search-res-tab">
            <button className="col-6" onClick={this.handleNextTrainClicked}>
              {this.props.t("next_trains_title")}
            </button>
            <button className="col-6 active">
              {this.props.t("analyse_my_trip_title")}
            </button>
          </div>
          <div className="row-filter d-flex" style={{ display: "flex" }}>
            <div className="col-sm-4">
              <h6>{this.props.t("delay_threshold")}</h6>
              <Select
                options={this.reliabOptions}
                value={this.state.selectedDataKey}
                onChange={this.handleMainDataKeyChanged}
                searchable
                noOptionsMessage={() => this.props.t("no_options")}
              />
            </div>
            <div className="col-sm-6">
              <h6>{this.props.t("year")}</h6>
              <Select
                options={this.state.years}
                value={this.state.selectedYear}
                onChange={this.handleYearChanged}
                placeholder={this.props.t("select_a_year")}
                noOptionsMessage={() => this.props.t("no_history")}
              />
            </div>
            <div className="col-sm-8 mt-2 m-sm-0">
              <h6>{this.props.t("rail_service")}</h6>
              <Select
                options={this.state.modes}
                value={this.state.selectedMode}
                onChange={this.handleModeChanged}
                searchable
                placeholder={this.props.t("select_a_service")}
                noOptionsMessage={() => this.props.t("no_service")}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12" style={{ marginTop: "2rem" }}>
              <h4 className="d-inline-block">
                {this.props.t("chart_reliability_by_hour")}
              </h4>
              {this.state.selectedMode && this.state.from && this.state.to && (
                <DownloadButton
                  link={GraphAPI.getODPerHourCSVExportLink(
                    this.state.from.id,
                    this.state.to.id,
                    this.state.selectedMode.label
                  )}
                  downloadName="per-hour.csv"
                />
              )}
              <br/>
              <br/>
              {this.state.isPerHourGraphLoading && (
                  <div className="alert alert-light">
                    <div className="mx-2">
                      {this.props.t("loading_withPeriod")}
                    </div>
                  </div>
                )}
              {this.state.showAlertHour && (
                  <div className="alert alert-danger">
                    <div className="mx-2">
                      {this.props.t("an_error_occurs")}
                      <br />
                      {this.props.t("cant_load_data")}
                    </div>
                  </div>
                )}
              {this.state.perHourSelectedYear === undefined &&
                this.state.perHourGraphData && (
                  <div className="alert alert-info">
                    <div className="mx-2">
                      {this.props.t("unavailable_data")}
                    </div>
                  </div>
                )}
              {this.state.isPerHourGraphLoading ||
                (this.state.perHourGraphData && this.state.perHourSelectedYear && (
                  <div style={{ height: "20rem" }}>
                    {this.state.perHourGraphData &&
                      this.state.selectedMode && this.state.perHourSelectedYear && (
                      <GraphOrigDestPerHour
                        perHourData={
                          this.state.perHourGraphData.hours_per_mode[
                            this.state.selectedMode.value
                          ].years[
                            this.state.perHourSelectedYear.value
                          ].data
                        }
                        dataKeysDef={selectedChartLines}
                      />
                    )}
                  </div>
                ))}
            </div>
          </div>
          <div className="row">
            <div className="col-12" style={{ marginTop: "3rem" }}>
              <h4 className="d-inline-block">
                {this.props.t("chart_reliability_by_day")}
              </h4>
              {this.state.selectedMode && this.state.from && this.state.to && (
                <DownloadButton
                  link={GraphAPI.getODPerDayCSVExportLink(
                    this.state.from.id,
                    this.state.to.id,
                    this.state.selectedMode.label
                  )}
                  downloadName="per-day.csv"
                />
              )}
              <br/>
              <br/>
              {this.state.isPerDayGraphLoading && (
                  <div className="alert alert-light">
                    <div className="mx-2">
                      {this.props.t("loading_withPeriod")}
                    </div>
                  </div>
                )}
              {this.state.showAlertDay && (
                  <div className="alert alert-danger">
                    <div className="mx-2">
                      {this.props.t("an_error_occurs")}
                      <br />
                      {this.props.t("cant_load_data")}
                    </div>
                  </div>
                )}
              {this.state.perDaySelectedYear === undefined &&
                this.state.perDayGraphData && (
                  <div className="alert alert-info">
                    <div className="mx-2">
                      {this.props.t("unavailable_data")}
                    </div>
                  </div>
                )}
              {this.state.isPerDayGraphLoading ||
                (this.state.perDayGraphData && this.state.perDaySelectedYear && (
                  <div style={{ height: "20rem" }}>
                    {this.state.perDayGraphData &&
                      this.state.selectedMode && this.state.perDaySelectedYear && (
                      <GraphOrigDestPerDay
                        perDayData={
                          this.state.perDayGraphData.days_per_mode[
                            this.state.selectedMode.value
                          ].years[
                            this.state.perDaySelectedYear.value
                          ].data
                        }
                        dataKeysDef={selectedChartLines}
                      />
                    )}
                  </div>
                ))}
            </div>
          </div>
          <div className="row">
            <div className="col-12" style={{ marginTop: "3rem" }}>
              <h4 className="d-inline-block">
                {this.props.t("chart_evol_of_reliability")}
              </h4>
              {(this.state.rangeSelectedYears) && (this.state.evolutionYears) && (this.state.evolutionYears.length > 1) &&
                <Box sx={{ width: "50%" }}>
                  <h6>Période sélectionnée</h6>
                  <Slider
                    value={this.state.rangeSelectedYears}
                    onChange={this.handleRangeSliderChanged}
                    min={Math.min(...this.state.evolutionYears?.map(yearDef => { return Number(yearDef.label) % 2000 }))}
                    max={Math.max(...this.state.evolutionYears?.map(yearDef => { return Number(yearDef.label) % 2000 }))}
                    scale={value => { return value + 2000 }}
                    marks={this.state.marks}
                    valueLabelDisplay="auto"
                    style={{ color: "#24356d" }}
                  />
                </Box>
              }
              {this.state.selectedMode && this.state.from && this.state.to && (
                <DownloadButton
                  link={GraphAPI.getODEvolutionCSVExportLink(
                    this.state.from.id,
                    this.state.to.id,
                    this.state.selectedMode.label
                  )}
                  downloadName="evolution.csv"
                />
              )}
              <br/>
              <br/>
              {this.state.isEvolutionGraphLoading && (
                <div className="alert alert-light">
                  <div className="mx-2">
                    {this.props.t("loading_withPeriod")}
                  </div>
                </div>
              )}
              {this.state.showAlertEvolution && (
                  <div className="alert alert-danger">
                    <div className="mx-2">
                      {this.props.t("an_error_occurs")}
                      <br />
                      {this.props.t("cant_load_data")}
                    </div>
                  </div>
                )}
              {this.state.isEvolutionGraphLoading ||
                (this.state.evolutionGraphData && (
                  <div style={{ height: "20rem" }}>
                    {this.state.selectedMode && this.state.rangeSelectedYears && (
                        <GraphOrigDestEvolution
                          evolutionData={
                            this.state.evolutionGraphData.evolutionPerMode[
                              this.state.selectedMode.value
                            ].years
                          }
                          rangeSelectedYears={this.state.rangeSelectedYears}
                          dataKeysDef={selectedChartLines}
                        />
                      )}
                  </div>
                ))}
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default withTranslation()(AnalyseJourneyPage);
